// grammar — the composer slot-layout that renders a D-062 socket (ASK-112 S4).
//
// Registered under the name "grammar" and selected by the COMPOSER when a fixture's catalogue
// socket carries an authored grammar (see `isGrammarSocket`), in preference to the manifest's
// `layoutAlgorithm`. That preference is the slice: placement stops being described twice — once
// as a socket and once as a manifest lane — and the socket wins, because it is the one measured
// off the geometry.
//
// This file is the THREE adapter only: it resolves the socket's domain into WORLD coordinates
// using the SAME transform the composer applied to the fixture body, runs the pure grammar
// (`packages/layout-grammar/src/grammar.js`), and maps placements onto the composer's slot shape
// ({position, quaternion}) so populateComics / open-close / browse-1d / hero-pickup consume it
// unchanged.
//
// ⚠️ The domain must be composed exactly as the BODY was, not merely by the anchor's yaw: the
// composer offsets the catalogue clone by (anchor.position − catalogue bbox-bottom-centre) and
// then yaws it about the anchor by `bodyYaw = (π − facingRad) + anchor.rotationY`. Composing the
// domain any other way would put the lane where the box ISN'T on any fixture whose facingDeg is
// not the box-family default — the failure would be invisible on demo-store and appear on the
// first rotated instance.

import * as THREE from "../../../../vendor/three.module.js";
import { registerSlotLayout } from "./index.js";
import { createThreeAdapter } from "../../../../../packages/layout-grammar/src/three.js";

// 📦 ASK-154 seam 2 (ASK-151 §6.7 ruling 1) — the world-space half of this adapter (the body transform, the
// socket's domain, its approach, the basis → quaternion) is the PACKAGE's (`@kapowx/layout-grammar/three`),
// bound here to kapowx's vendored THREE, so the GCG console's reviewer card seats sample products with the
// very maths that seats the shop. What stays here is the COMPOSER's half: the slot shape populateComics reads,
// and the presentation extras (lane centre, hero extract, scene-truth region/volume/panel).
// The three CLASSES, never the namespace: handing `THREE` itself over as a value makes the bundler keep every
// export of three.js (it can no longer see which are read) — measured +6.6 KB of customer-initial JS.
const A = createThreeAdapter({ Vector3: THREE.Vector3, Quaternion: THREE.Quaternion, Matrix4: THREE.Matrix4 });
export const { fixtureLocalFront, socketLocalFront, grammarApproach, resolveDomain } = A;
const V3 = (p) => new THREE.Vector3(p.x, p.y, p.z);

function computeGrammarLayout(ctx) {
	const { anchor, socket, manifest, variant, slotDef, products = [], productTypes, catalogueFrame, countOverride } = ctx;
	const empty = { slots: [], extras: { slotCount: 0, maxSlots: 0 } };
	if (!socket) return empty;

	// ASK-147 §13.14 item 1 — EVERY grammar domain is approached from the fixture's OWN front, turned with
	// the body (`grammarApproach`). This was the volume's rule alone (ASK-139 F-139an: the crate faced every
	// sleeve at an END PANEL); spline and surface sockets were handed the composer's `frontDir`, the box
	// family's −X default (`facingDeg ?? 180`), and on dev H3's book bay drew all 54 books with the spine to
	// the wall (§13.12: spine·approach −1.000). The composer's `frontDir` no longer feeds a grammar socket.
	// ASK-147 §14.10 — …unless the SOCKET publishes its own frame: then it is approached from its own front
	// (`socketLocalFront`), so a double-sided island's B side browses from B's aisle. Frameless sockets: unchanged.
	// Both live in the package's `seatSocket` now — the one call the console's inspector makes too.
	const seat = A.seatSocket({
		socket, anchor, catalogueFrame, manifest, products, productTypes,
		maxItems: Number.isFinite(countOverride) && countOverride > 0 ? countOverride : Infinity,
	});
	const plan = seat.plan;
	const approachV = seat.approach;
	const approach = { x: approachV.x, y: approachV.y, z: approachV.z };

	if (!seat.ok) {
		// Loud, and inert: the composer falls back to the manifest algorithm rather than blanking
		// a shelf. A socket the grammar cannot draw is a data problem to fix, not a scene to lose.
		console.warn(`[grammar] ${anchor?.name}:${socket.id} — ${seat.reason}; deferring to the manifest layout`);
		return null;
	}
	for (const note of plan.notes) console.log(`[grammar] ${anchor?.name}:${socket.id} — ${note}`);

	// A slot carries its ADDRESS as well as its pose. On a continuous domain that is what makes the
	// non-drag pin path possible at all: a merchant who selects a seated item and picks `Pin…` pins it
	// exactly where it already stands, writing the byte-identical row a drag to the same spot would —
	// which is WCAG 2.5.7 satisfied by construction rather than by a parallel path (ASK-120 S3.6).
	// A VOLUME seat also carries its BIN — which column it stands in, how much room the box leaves in
	// front of and behind it, and the box's depth/across directions — because a crate is browsed one
	// column at a time and the browse gesture tips a sleeve inside that room (ASK-139 F-139an).
	const vf = plan.frame?.kind === "volume" ? plan.frame : null;
	const slots = plan.placements.map((pl, i) => ({
		...seat.slots[i],
		...(vf && pl.cell ? { bin: {
			key: `${anchor?.name || ""}::${socket.id ?? ""}#${pl.cell.layer}:${pl.cell.col}`,
			col: pl.cell.col, row: pl.cell.row, layer: pl.cell.layer, room: pl.room,
			depth: V3(vf.tangent), across: V3(vf.bitangent),
			dims: pl.dims ? { w: pl.dims.w, h: pl.dims.h, d: pl.dims.d, source: pl.dims.source ?? null } : null,
		} } : {}),
		// A PANEL seat carries its cell and the listing's size (ASK-147 §5.4) — the wall display sizes
		// the sleeve by the very numbers the grid packed it by, as the bin does.
		...(plan.panel && pl.cell ? { panelCell: {
			col: pl.cell.col, row: pl.cell.row,
			dims: pl.dims ? { w: pl.dims.w, h: pl.dims.h, d: pl.dims.d, source: pl.dims.source ?? null } : null,
		} } : {}),
	}));
	// WHICH products these slots are for, in slot order. Every distribution before `scatter` seated a
	// PREFIX of the pool, so the composer could assume `products[0..n]` — `scatter` can refuse an
	// individual row on `fit` and re-orders by region, so the assumption had to become a statement.
	// For the prefix layouts this is exactly [0,1,2,…] and nothing changes.
	const seatOrder = plan.placements.map((pl) => pl.index);

	// ── Capacity ────────────────────────────────────────────────────────────────────────────
	// The published `capacity` is the mint's umbrella-derived shim (S2 labels it
	// `capacitySource: "derived"`); the honest number for THIS stock is the domain length divided
	// by what these items actually spend, which is why a lane holds more 3 mm floppies than 8 mm
	// trades. Reference capacity is kept beside it so a drift between the two is visible.
	const firstPitch = plan.placements[0]?.pitch || 0;
	const domainLength = plan.frame?.length || 0;
	// `scatter` has no single pitch to divide by — its honest packed number is what the packer actually
	// seated once the region turned stock away. Below saturation the region is not full, so the
	// reference capacity is still the better answer to "how many does this hold".
	const packedCapacity = (plan.distribution === "run" && domainLength > 0 && firstPitch > 0)
		? Math.floor(domainLength / firstPitch)
		: (plan.distribution === "scatter" && plan.saturated) ? plan.count
		: plan.capacity;
	const maxSlots = Math.max(slots.length, packedCapacity || 0);

	// ── Presentation extras ─────────────────────────────────────────────────────────────────
	// laneCenter / laneSize / heroExtract are PRESENTATION, not placement: where a picked-up item
	// flies to and how big the lane reads. D-062 has no presentation vocabulary, so these keep
	// their manifest-slot source and stay byte-identical to the pre-grammar render; only the SLOTS
	// move to the socket. Flagged to Coord — an extract distance belongs to the pose preset.
	const vol = variant?.volume || slotDef?.volume || (Array.isArray(manifest?.slots) ? manifest.slots[0]?.volume : null) || {};
	const p0 = plan.placements[0]?.position;
	const p1 = plan.placements[1]?.position;
	const step = (p0 && p1) ? V3(p1).sub(V3(p0))
		: new THREE.Vector3(plan.frame.tangent.x, plan.frame.tangent.y, plan.frame.tangent.z).multiplyScalar(firstPitch || 0);
	const laneCenter = anchor.position.clone();
	const laneLength = Number.isFinite(vol.w) ? vol.w : (domainLength || 0.12);
	const laneHeight = Number.isFinite(vol.h) ? vol.h : 0.3;
	const laneDepth  = Number.isFinite(vol.d) ? vol.d : 0.2;
	const itemH = plan.placements[0]?.dims?.h || 0.26;
	const laneDir = approachV.clone();
	const heroExtract = laneCenter.clone()
		.addScaledVector(laneDir, laneDepth * 0.5 + 0.18)
		.add(new THREE.Vector3(0, itemH * 0.85, 0));

	return {
		slots,
		extras: {
			slotCount: slots.length, maxSlots, seatOrder, socketId: socket.id ?? null,
			laneCenter, laneSize: new THREE.Vector3(laneLength, laneHeight, laneDepth),
			slotStepVector: step, slotQuaternion: slots[0]?.quaternion?.clone() || new THREE.Quaternion(),
			heroExtract,
			// The REGION, as scene truth. `stage.hitTestPinTarget` turns a screen pixel over this table
			// into the `{u,v}` under the cursor, so a drop writes the address the merchant can see —
			// and the probe reads it to assert the packing without re-deriving any of it.
			region: plan.frame?.kind === "surface" ? {
				anchorName: anchor?.name || null, socketId: socket.id ?? null,
				uvOrigin: plan.frame.uvOrigin, uAxis: plan.frame.uAxis, vAxis: plan.frame.vAxis,
				normal: plan.frame.normal, polygonUV: plan.frame.polygonUV, blockedUV: plan.frame.blockedUV || [],
				bounds: plan.frame.bounds, points: plan.frame.points,
				area: +(plan.frame.area || 0).toFixed(5), convex: plan.frame.convex !== false,
				regionExact: plan.frame.regionExact !== false,
			} : null,
			// The VOLUME, as scene truth (ASK-139 F-139an): the box the layout packed, in world — the
			// render leg asks "is every sleeve inside the bin" against exactly this, never a re-derivation.
			volume: vf ? {
				anchorName: anchor?.name || null, socketId: socket.id ?? null,
				origin: vf.origin, depth: vf.tangent, across: vf.bitangent, up: vf.normal,
				length: +vf.length.toFixed(5), width: +vf.width.toFixed(5), height: +vf.height.toFixed(5),
				corners: vf.corners, approach,
			} : null,
			// The PANEL, as scene truth (ASK-147 §5.4) — a wall bay's frame and the box in front of it, so
			// the render leg asks "is every sleeve inside the bay, upright, cover out" against the frame
			// the layout used. Present only on a non-level surface; a level top carries no such key.
			...(plan.panel ? { panel: {
				anchorName: anchor?.name || null, socketId: socket.id ?? null,
				origin: plan.panel.origin, right: plan.panel.right, up: plan.panel.up, normal: plan.panel.normal,
				width: +plan.panel.width.toFixed(5), height: +plan.panel.height.toFixed(5),
			} } : {}),
			approach: vf ? approachV.clone() : null,
			grammar: {
				domain: socket.domain, distribution: plan.distribution, pose: plan.pose, typeId: plan.typeId,
				domainLength: +domainLength.toFixed(4), pitch: +firstPitch.toFixed(5),
				capacityPacked: packedCapacity, capacityReference: plan.capacity, capacityPublished: plan.capacityPublished,
				saturated: plan.saturated === true, pinnedCount: plan.placements.filter((p) => p.pinned).length,
				regionArea: plan.frame?.kind === "surface" ? +(plan.frame.area || 0).toFixed(5) : null,
				dimsSource: plan.placements[0]?.dimsSource || null,
			},
		},
	};
}

registerSlotLayout("grammar", computeGrammarLayout);
export { computeGrammarLayout };
